import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import ClientLayout from '../../components/ClientLayout';

const COLORS = {
    primary: '#1F2937',
    secondary: '#3B82F6',
    success: '#10B981',
    bg: '#F9F9F5',
    white: '#FFFFFF',
    danger: '#EF4444'
};

function EstadoCuenta() {
    const [loading, setLoading] = useState(true);
    const [resumen, setResumen] = useState({ saldoPorPagar: 0 });
    const [pagos, setPagos] = useState([]);
    const [pedidos, setPedidos] = useState([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            try {
                const [resumenRes, pagosRes, pedidosRes] = await Promise.all([
                    api.get('/pagos/resumen', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get('/pagos/me', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get('/pedidos?limit=100', { headers: { Authorization: `Bearer ${token}` } }) 
                ]);

                // 1. FILTRO ESTRICTO: Solo pedidos ENTREGADOS para el Estado de Cuenta
                const listaPedidos = pedidosRes.data.data || [];
                const entregados = listaPedidos
                    .filter(p => p.estado === 'ENTREGADO' && p.estado !== 'CANCELADO')
                    .sort((a, b) => new Date(b.fechaEntrega || b.createdAt) - new Date(a.fechaEntrega || a.createdAt));
                
                setPedidos(entregados);

                // 2. PAGOS: Ordenar por fecha (Reciente primero)
                const listaPagos = pagosRes.data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
                setPagos(listaPagos);

                // 3. RECÁLCULO DE SALDO DINÁMICO (Basado en Entregados)
                const deudaEntregada = entregados.reduce((acc, p) => acc + Number(p.montoTotal), 0);
                const totalAbonado = listaPagos.reduce((acc, pago) => acc + Number(pago.cantidad), 0);
                
                setResumen({
                    ...resumenRes.data,
                    saldoPorPagar: deudaEntregada - totalAbonado
                });

            } catch (error) {
                console.error("Error cargando finanzas:", error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // Cálculo del rango de fechas del periodo (Primer entrega vs Última entrega)
    const fechaInicio = pedidos.length > 0 
        ? new Date(pedidos[pedidos.length - 1].fechaEntrega || pedidos[pedidos.length - 1].createdAt).toLocaleDateString() 
        : 'N/A';
    const fechaFin = pedidos.length > 0 
        ? new Date(pedidos[0].fechaEntrega || pedidos[0].createdAt).toLocaleDateString() 
        : new Date().toLocaleDateString();

    const handleExportPDF = async () => {
        setExporting(true);
        const token = localStorage.getItem('token');
        try {
            const response = await api.get('/pagos/exportar-pdf', {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `EdoCuenta_Xavana_${fechaFin.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            alert('Error al generar el PDF.');
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <ClientLayout title="Estado de Cuenta">Cargando...</ClientLayout>;

    const saldo = resumen?.saldoPorPagar || 0;

    return (
        <ClientLayout title="Estado de Cuenta">
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                
                {/* HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 25 }}>
                    <div>
                        <h2 style={{ color: COLORS.primary, margin: 0 }}>Estado de Cuenta</h2>
                        <p style={{ color: '#666', margin: 0 }}>
                            Periodo: <strong>{fechaInicio}</strong> al <strong>{fechaFin}</strong>
                        </p>
                    </div>
                    <button 
                        onClick={handleExportPDF} 
                        disabled={exporting || pedidos.length === 0}
                        style={{ background: COLORS.primary, color: 'white', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        {exporting ? 'Generando PDF...' : '📄 Descargar Resumen (PDF)'}
                    </button>
                </div>

                {/* BANNER DE SALDO */}
                <div style={{ 
                    background: saldo > 0 ? COLORS.secondary : COLORS.success, 
                    color: 'white', padding: '40px 20px', borderRadius: 16, 
                    textAlign: 'center', marginBottom: 30
                }}>
                    <span style={{ textTransform: 'uppercase', fontSize: 14, opacity: 0.8 }}>Saldo Real (Mercancía Entregada)</span>
                    <div style={{ fontSize: 52, fontWeight: '800', margin: '5px 0' }}>
                        ${saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </div>
                </div>

                {/* 🆕 SECCIÓN: ÚLTIMO PAGO RECIBIDO */}
                {pagos.length > 0 && (
                    <div style={{ 
                        background: '#F0FDF4', 
                        border: `1px solid #BBF7D0`, 
                        padding: '20px', 
                        borderRadius: 12, 
                        marginBottom: 30, 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                    }}>
                        <div>
                            <span style={{ color: '#166534', fontWeight: 'bold', fontSize: 13 }}>✅ ÚLTIMO PAGO RECIBIDO</span>
                            <div style={{ color: COLORS.primary, fontSize: 18, fontWeight: 'bold' }}>
                                ${Number(pagos[0].cantidad).toLocaleString('es-MX')}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#666', fontSize: 12 }}>Fecha: {new Date(pagos[0].fecha).toLocaleDateString()}</div>
                            <div style={{ color: '#666', fontSize: 12 }}>Referencia: {pagos[0].referencia || 'N/A'}</div>
                        </div>
                    </div>
                )}

                {/* TABLA DETALLADA CON DESGLOSE */}
                <h3 style={{ color: COLORS.primary, marginBottom: 15 }}>Detalle de Entregas</h3>
                <div style={{ background: 'white', borderRadius: 12, border: `1px solid #E5E7EB`, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#F9FAFB' }}>
                            <tr>
                                <th style={thStyle}>Folio / Sucursal</th>
                                <th style={thStyle}>Fecha Entrega</th>
                                <th style={thStyle}>Producto / Desglose</th>
                                <th style={thStyle}>Monto</th>
                                <th style={thStyle}>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pedidos.map(p => (
                                <tr key={p.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                    <td style={tdStyle}>
                                        <div style={{ fontWeight: 'bold' }}>...{p.id.slice(-6)}</div>
                                        <div style={{ fontSize: 12, color: '#6B7280' }}>{p.sucursal?.nombre}</div>
                                    </td>
                                    <td style={tdStyle}>
                                        {p.fechaEntrega ? new Date(p.fechaEntrega).toLocaleDateString() : 'Confirmando...'}
                                    </td>
                                    <td style={tdStyle}>
                                        <div style={{ fontWeight: '500' }}>{p.tipoProducto.replace(/_/g, ' ')}</div>
                                        {/* ✅ DESGLOSE RESTAURADO */}
                                        <div style={{ fontSize: 11, color: COLORS.secondary, fontWeight: 'bold' }}>
                                            {p.cantidadGrano}kg Grano / {p.cantidadMolido}kg Molido
                                        </div>
                                    </td>
                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                                        ${Number(p.montoTotal).toLocaleString('es-MX')}
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{ 
                                            padding: '4px 8px', borderRadius: 20, fontSize: 10, fontWeight: 'bold',
                                            background: p.pagado ? '#D1FAE5' : '#FEE2E2',
                                            color: p.pagado ? '#065F46' : '#991B1B'
                                        }}>
                                            {p.pagado ? 'PAGADO' : 'PENDIENTE'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </ClientLayout>
    );
}

const thStyle = { padding: '15px', textAlign: 'left', fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '15px', fontSize: 14, color: '#1F2937' };

export default EstadoCuenta;